import { ResponseDocumentSubtype, SUBTYPE_OTHER_NAME } from '../../models/response-document-subtype';
import { ResponseProjectUploadFile } from '../../models/response-project-document';

export const UNCATEGORIZED_COLOR = '#D97706';

export const INTERNAL_DOCUMENT_TYPE_ORDER: readonly string[] = [
  'memoire_technique_anterieur',
  'reference',
  'equipe_cv',
  'presentation_entreprise',
  'doctrine_metier',
  'justificatif_entreprise',
  'fiche_produit',
  'autre',
];

const TYPE_COLORS: Record<string, string> = {
  memoire_technique_anterieur: '#1B3A6B',
  reference: '#2563EB',
  equipe_cv: '#7C3AED',
  presentation_entreprise: '#E8590C',
  doctrine_metier: '#0891B2',
  justificatif_entreprise: '#2F9E44',
  fiche_produit: '#DB2777',
  autre: '#64748B',
};

export function getDocumentTypeColor(name?: string | null): string {
  if (!name) return UNCATEGORIZED_COLOR;
  return TYPE_COLORS[name] ?? TYPE_COLORS['autre'];
}

export function sortInternalDocumentSubtypes<T extends { name: string }>(subtypes: T[]): T[] {
  const rank = new Map(INTERNAL_DOCUMENT_TYPE_ORDER.map((name, i) => [name, i]));
  return [...subtypes].sort((a, b) => {
    const ra = rank.get(a.name) ?? INTERNAL_DOCUMENT_TYPE_ORDER.length;
    const rb = rank.get(b.name) ?? INTERNAL_DOCUMENT_TYPE_ORDER.length;
    if (ra !== rb) return ra - rb;
    return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
  });
}

export function findUploadSubtype(
  file: Pick<ResponseProjectUploadFile, 'subtype' | 'subtypeId' | 'customName'>,
  options: ResponseDocumentSubtype[]
): ResponseDocumentSubtype | undefined {
  if (file.subtypeId != null) return options.find(o => o.id === file.subtypeId);
  if (!file.subtype) return undefined;
  return options.find(o => o.displayedName === file.subtype || o.name === file.subtype);
}

export function uploadSubtypeSlug(
  file: Pick<ResponseProjectUploadFile, 'subtype' | 'subtypeId' | 'customName'>,
  options: ResponseDocumentSubtype[]
): string | null {
  const opt = findUploadSubtype(file, options);
  if (opt && opt.name !== 'undefined') return opt.name;
  return file.subtype ?? null;
}

export function uploadSubtypeDisplayName(
  file: Pick<ResponseProjectUploadFile, 'subtype' | 'subtypeId' | 'customName'>,
  options: ResponseDocumentSubtype[]
): string {
  const opt = findUploadSubtype(file, options);
  if (opt && opt.name !== 'undefined') {
    if (opt.name === SUBTYPE_OTHER_NAME && file.customName) return file.customName;
    return opt.displayedName;
  }
  return file.customName ?? file.subtype ?? '';
}

const FILE_EXT_COLORS: Record<string, string> = {
  pdf: '#D6453E',
  docx: '#2B6CD6',
  doc: '#2B6CD6',
  pptx: '#E07A2B',
  ppt: '#E07A2B',
  xlsx: '#1F9D5B',
  xls: '#1F9D5B',
};

export function getFileExtension(name: string): string {
  const ext = name.includes('.') ? (name.split('.').pop() ?? '') : '';
  return ext.slice(0, 4).toLowerCase();
}

export function getFileExtensionColor(name: string): string {
  return FILE_EXT_COLORS[getFileExtension(name)] ?? '#6B7280';
}
